import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, concatMap, mergeMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { KirsActions } from './kirs.actions';
import { KirsService } from '../../service/kirs.service';
import { ManageCampaignsKirsActions } from '../manage-campaigns-kirs/manage-campaigns-kirs.actions';


@Injectable()
export class KirsEffects {

  loadKirs$ = createEffect(
    (rs: KirsService = inject(KirsService)) => {
      return this.actions$.pipe(

        ofType(KirsActions.loadKirs),
        mergeMap(({ campaignId, parameters }) =>
          rs.getKirs(campaignId, parameters).pipe(
            map((kirs) => {
              return KirsActions.loadKirsSuccess({
                kirs
              });
            }),
            catchError((error) => of(KirsActions.loadKirsFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  loadAllKirs$ = createEffect(
    (rs: KirsService = inject(KirsService)) => {
      return this.actions$.pipe(

        ofType(KirsActions.loadAllKirs),
        mergeMap(({ campaignId, parameters }) =>
          rs.getKirs(campaignId, parameters).pipe(
            map((allKirs) => {
              return KirsActions.loadAllKirsSuccess({
                allKirs
              });
            }),
            catchError((error) => of(KirsActions.loadAllKirsFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  getCampaign$ = createEffect(
    (rs: KirsService = inject(KirsService)) => {
      return this.actions$.pipe(

        ofType(KirsActions.getCampaigns),
        mergeMap(() =>
          rs.getCampaigns().pipe(
            map((campaigns) => {
              return KirsActions.getCampaignsSuccess({
                campaigns
              });
            }),
            catchError((error) => of(KirsActions.getCampaignsFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  updateKirs$ = createEffect(
    (rs: KirsService = inject(KirsService)) => {
      return this.actions$.pipe(
        ofType(KirsActions.updateKirs),
        mergeMap(({ campaignId, parameters, input }) =>
          rs.updateKirs(campaignId, input).pipe(
            mergeMap((update) => [
              KirsActions.updateKirsSuccess({ update }),
              KirsActions.loadAllKirs({ campaignId, parameters })

            ]),
            catchError((error) =>
              of(KirsActions.updateKirsFailure({ error, id: input?.id as string }))
            )
          )
        )
      );
    },
    { functional: true }
  );

  createOrUpdateCampaigns$ = createEffect(
    (rs: KirsService = inject(KirsService)) => {
      return this.actions$.pipe(
        ofType(KirsActions.createOrUpdateCampaign),
        concatMap(({ campaignId, year, legalEntityTrackingIds, parameters }) =>
          rs.createOrUpdateCampaigns(campaignId, year as number, legalEntityTrackingIds).pipe(
            mergeMap((createOrUpdate) => {
              const actualCampaignId = createOrUpdate?.id || campaignId;
              return [
                KirsActions.createOrUpdateCampaignSuccess({
                  createOrUpdate
                }),
                KirsActions.loadAllKirs({ campaignId: actualCampaignId, parameters }),
                ManageCampaignsKirsActions.loadLegalEntities({ campaignId: actualCampaignId, parameters })
              ];
            }),
            catchError((error) =>
              of(KirsActions.createOrUpdateCampaignFailure({ error }))
            )
          )
        )
      );
    },
    { functional: true }
  );

  deleteCampaigns$ = createEffect(
    (rs: KirsService = inject(KirsService)) => {
      return this.actions$.pipe(

        ofType(KirsActions.closeCampaign),
        mergeMap(({ campaignId }) =>
          rs.closeCampaign(campaignId).pipe(
            map((closeCampaign) => {
              return KirsActions.closeCampaignSuccess({
                closeCampaign
              });
            }),
            catchError((error) => of(KirsActions.closeCampaignFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  closeCampaignSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(KirsActions.closeCampaignSuccess),
        mergeMap(({ closeCampaign }) => {
          const campaignId = closeCampaign?.id;
          const defaultParameters = {
            page: 0,
            order: {
              sortField: null,
              sortDirection: "ASC"
            },
            search: null,
            filters: {},
            rows: 100000
          };

          const manageCampaignParameters = {
            campaignId: campaignId as string,
            page: {
              pageNo: 0,
              pageSize: 100000
            }
          };

          return [
            KirsActions.getCampaigns(), // Aggiorna la lista delle campagne per vedere il nuovo status
            KirsActions.loadAllKirs({
              campaignId: campaignId as string,
              parameters: defaultParameters
            }),
            KirsActions.loadKirs({
              campaignId: campaignId as string,
              parameters: { ...defaultParameters, rows: 10 }
            }),
            ManageCampaignsKirsActions.loadLegalEntities({
              campaignId: campaignId as string,
              parameters: manageCampaignParameters
            })
          ];
        })
      );
    },
    { functional: true }
  );


  exportKirs$ = createEffect(
    (ks: KirsService = inject(KirsService)) => {
      return this.actions$.pipe(
        ofType(KirsActions.exportKirs),
        mergeMap(({ campaignId, parameters, timeZone }) =>
          ks.exportKirs(campaignId, timeZone, parameters).pipe(
            map((file) => {
              // Download diretto del file (se presente)
              if (file?.data && file.data.length > 0) {
                const f = file.data[0];
                const byteChars = atob(f!.content);
                const byteNumbers = new Array(byteChars.length);
                for (let i = 0; i < byteChars.length; i++) {
                  byteNumbers[i] = byteChars.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
                const link = window.document.createElement("a");
                link.href = window.URL.createObjectURL(blob);
                link.download = f!.filename ?? "kirs.xlsx";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(link.href);
              }
              return KirsActions.exportKirsSuccess({
                export: file
              });
            }),
            catchError((error) => of(KirsActions.exportKirsFailure({ error })))
          )
        )
      )
    }
  );


  constructor(private actions$: Actions) { }
}
